import SearchIcon from '@mui/icons-material/Search';
import MaterialAutocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { ChangeEvent, MutableRefObject, useEffect, useRef, useState } from 'react';
import { SearchResult } from '../../api/api';
import { useLazySearchQuery } from '../../api/apiService';
import { useAppSelector } from '../../app/hooks';

interface AutocompleteProps {
  onSelect: (data: SearchResult) => void;
  size?: 'small' | 'medium';
  width?: string;
}

export default function Autocomplete({ onSelect, size, width = "18.75rem" }: AutocompleteProps) {
  const configId = useAppSelector((state) => state.config?.id);
  const searchResult = useAppSelector((state) => state.searchResult);
  const [inputValue, setInputValue] = useState(searchResult?.displayName || searchResult?.value || '');
  const [defaultValue] = useState(searchResult);
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [search] = useLazySearchQuery();
  const inputRef: MutableRefObject<HTMLInputElement | undefined> = useRef<HTMLInputElement>();

  async function getData(query: string) {
    setLoading(true);
    const { data } = await search({ query, configId: configId! }, true);
    setOptions(data || []);
    setLoading(false);
    return data;
  }

  function customMatch(text: string, query: string) {
    const results: [number, number][] = [];
    const trimmedQuery = query.trim().toLowerCase();
    const textLower = text.toLowerCase();
    const queryLength = trimmedQuery.length;
    let indexOf = textLower.indexOf(trimmedQuery);
    while (indexOf > -1) {
      results.push([indexOf, indexOf + queryLength]);
      indexOf = textLower.indexOf(query, indexOf + queryLength);
    }
    return results;
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  useEffect(() => {
    (async () => {
      const query = searchResult?.displayName || searchResult?.value;
      if (query && configId) {
        const { data } = await search({ query, configId, source: searchResult?.source }, true);
        setOptions(data || []);
      }
    })();
  }, [configId, searchResult, search]);

  return (
    <Grid container>
      <Grid item>
        <MaterialAutocomplete
          openOnFocus
          style={{ width }}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.displayName)}
          filterOptions={(x) => x}
          options={options}
          freeSolo
          autoComplete
          includeInputInList
          loadingText="Hämtar"
          defaultValue={defaultValue}
          size={size}
          loading={loading}
          onChange={async (_event, newInputValue, reason) => {
            if (newInputValue && reason === 'selectOption')
              onSelect(newInputValue as SearchResult);
            else if (newInputValue && reason === 'createOption') {
              await getData(inputValue);
              inputRef.current?.blur();
              inputRef.current?.focus();
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={input => {
                if (input)
                  inputRef.current = input;
              }}
              variant="outlined"
              fullWidth
              onChange={handleChange}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, option) => {
            let matches = match(option.displayName, inputValue);

            if (matches?.length < 1 && option.displayName && inputValue)
              matches = customMatch(option.displayName, inputValue);

            const parts = parse(
              option.displayName,
              matches
            );

            return (
              <Grid container alignItems="center"  {...props as any}>
                <Grid item>
                </Grid>
                <Grid item xs>
                  {parts.map((part, i) => (
                    <span key={i} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                      {part.text}
                    </span>
                  ))}
                  <Typography variant="body2" color="textSecondary">
                    {option.source} | {option.type}
                  </Typography>
                </Grid>
              </Grid>
            );
          }}
        />
      </Grid>
      {size !== 'small' &&
        <Grid item style={{ display: 'flex' }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            aria-label="Sök"
            onClick={async () => {
              await getData(inputValue);
              inputRef.current?.focus();
            }}
          >
            Sök
          </Button>
        </Grid>
      }
    </Grid>
  );
}
